import React from "react"

// Top-nav icons reproduced 1:1 from the Willow Create design system: thin-stroke,
// 24-grid, round caps/joins, stroke = currentColor so they inherit the nav/button
// color. Drop-in for both NavItemIcon (rendered as an element) and IconButton
// (rendered as a component via React.createElement). Each icon forwards only
// size/className/style — IconButton also injects color/isActive/strokeWidth, which
// we deliberately drop so the stroke width stays at the design's value.

type IconProps = {
  size?: number
  className?: string
  style?: React.CSSProperties
}

const Svg: React.FC<IconProps & { strokeWidth?: number; children: React.ReactNode }> = ({
  size = 20,
  className,
  style,
  strokeWidth = 1.8,
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
  >
    {children}
  </svg>
)

export const HomeIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
  </Svg>
)

export const CalendarIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <rect x={3} y={5} width={18} height={16} rx={2} />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Svg>
)

export const SparklesIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <path d="M12 3l1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7Z" />
    <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9Z" />
  </Svg>
)

export const EyeIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
    <circle cx={12} cy={12} r={2.6} />
  </Svg>
)

export const CompassIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <circle cx={12} cy={12} r={9} />
    <path d="M15.5 8.5 13.6 13.6 8.5 15.5l1.9-5.1Z" />
  </Svg>
)

export const AnalyticsIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <circle cx={12} cy={12} r={9} />
    <path d="M12 3v9l6.5 4" />
  </Svg>
)

export const DraftsIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </Svg>
)

// Question mark from Heroicons v2 (outline question-mark-circle), drawn through our
// Svg wrapper so it inherits the design's 1.8 stroke and round caps. Its circle is
// r=9 centered on the grid, so it lines up with the other r=9 nav icons.
export const HelpIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style}>
    <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75m0 3h.008v.008H12v-.008ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </Svg>
)

// The add/new-post plus is the one icon the design draws bolder (stroke 2.2).
export const PlusIcon: React.FC<IconProps> = ({ size, className, style }) => (
  <Svg size={size} className={className} style={style} strokeWidth={2.2}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)
